import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./RoleEditPage.module.css";
import { useLanguage } from "../../context/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIconPack } from "../../context/IconPackContext";
import { ToastService } from "../../service/toast.service";
import { deleteUser, postUser } from "../../helper/http";
import { IconButton, Checkbox } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarIcon from "@mui/icons-material/Star";

interface Module {
  id: number;
  name: string;
  table_name: string;
}

interface RolePermissions {
  [moduleId: string]: {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
  };
}

export default function RoleEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { getIcon } = useIconPack();

  const [role, setRole] = useState<any>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [reports, setReports] = useState<Module[]>([]);
  const [dashboards, setDashboards] = useState<Module[]>([]);
  const [permissions, setPermissions] = useState<RolePermissions>({});
  const [allAccess, setAllAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const response = await postUser("crud/list", {
          module: "role",
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "id",
          filters: {
            id: Number(id),
          },
        });

        if (response.data && response.data.length > 0) {
          setRole(response.data[0]);
        }
      } catch (error) {
        console.error("Failed to fetch role:", error);
        ToastService.error(t("errorLoadingRole"));
      }
    };

    fetchRole();
  }, [id, t]);

  useEffect(() => {
    const fetchModules = async () => {
      if (!role) return;
      try {
        setLoading(true);
        const response = await postUser("crud/list", {
          module: "module",
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "id",
        });
        const response2 = await postUser("crud/list", {
          module: "report",
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "id",
        });
        const response3 = await postUser("crud/list", {
          module: "dashboard",
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "id",
        });

        const responseAcc = await postUser("crud/list", {
          filters: {
            role_id: role.id,
          },
          module: "role_access",
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "id",
        });

        const moduleMap: Record<string, string> = {};

        for (const acc of responseAcc.data) {
          if (!acc.module) {
            moduleMap["*"] = "*";
          } else {
            const pref = acc.module === "module" ? "" : acc.module + "_";

            moduleMap[pref + acc.module_id] = acc.access;
          }
        }

        setModules(response.data);
        setReports(response2.data);
        setDashboards(response3.data);

        const initialPermissions: RolePermissions = {};
        response.data.forEach((module: Module) => {
          if (moduleMap["*"]) {
            initialPermissions[module.id] = {
              create: true,
              read: true,
              update: true,
              delete: true,
            };
          } else {
            const pref = "";
            const access = moduleMap[pref + module.id];

            if (access) {
              initialPermissions[module.id] = {
                create: access.indexOf("create") >= 0,
                read: access.indexOf("read") >= 0,
                update: access.indexOf("update") >= 0,
                delete: access.indexOf("delete") >= 0,
              };
            } else {
              initialPermissions[module.id] = {
                create: false,
                read: false,
                update: false,
                delete: false,
              };
            }
          }
        });
        response2.data.forEach((module: Module) => {
          const pref = "report";
          if (moduleMap["*"]) {
            initialPermissions[pref + "_" + module.id] = {
              create: true,
              read: true,
              update: true,
              delete: true,
            };
          } else {
            const access = moduleMap[pref + "_" + module.id];

            if (access) {
              initialPermissions[pref + "_" + module.id] = {
                create: access.indexOf("create") >= 0,
                read: access.indexOf("read") >= 0,
                update: access.indexOf("update") >= 0,
                delete: access.indexOf("delete") >= 0,
              };
            } else {
              initialPermissions[pref + "_" + module.id] = {
                create: false,
                read: false,
                update: false,
                delete: false,
              };
            }
          }
        });
        response3.data.forEach((module: Module) => {
          const pref = "dashboard";
          if (moduleMap["*"]) {
            initialPermissions[pref + "_" + "_" + module.id] = {
              create: true,
              read: true,
              update: true,
              delete: true,
            };
          } else {
            const access = moduleMap[pref + "_" + module.id];

            if (access) {
              initialPermissions[pref + "_" + module.id] = {
                create: access.indexOf("create") >= 0,
                read: access.indexOf("read") >= 0,
                update: access.indexOf("update") >= 0,
                delete: access.indexOf("delete") >= 0,
              };
            } else {
              initialPermissions[pref + "_" + module.id] = {
                create: false,
                read: false,
                update: false,
                delete: false,
              };
            }
          }
        });

        setAllAccess(moduleMap["*"] === "*");
        setPermissions(initialPermissions);
      } catch (error) {
        console.error("Failed to fetch modules:", error);
        ToastService.error(t("errorLoadingModules"));
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, [t, role]);

  const handleAllAccessToggle = () => {
    const newAllAccess = !allAccess;
    setAllAccess(newAllAccess);

    const updatedPermissions: RolePermissions = {};
    modules.forEach((module) => {
      updatedPermissions[module.id] = {
        create: newAllAccess,
        read: newAllAccess,
        update: newAllAccess,
        delete: newAllAccess,
      };
    });
    reports.forEach((module) => {
      updatedPermissions["report_" + module.id] = {
        create: newAllAccess,
        read: newAllAccess,
        update: newAllAccess,
        delete: newAllAccess,
      };
    });
    dashboards.forEach((module) => {
      updatedPermissions["dashboard_" + module.id] = {
        create: newAllAccess,
        read: newAllAccess,
        update: newAllAccess,
        delete: newAllAccess,
      };
    });
    setPermissions(updatedPermissions);
  };

  const handlePermissionChange = (
    moduleId: string | number,
    permission: "create" | "read" | "update" | "delete",
  ) => {
    setPermissions((prev) => {
      const updated: RolePermissions = {
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          [permission]: !prev[moduleId][permission],
        },
      };

      const allEnabled =
        modules.every((module) => {
          const perms = updated[module.id];
          return (
            perms && perms.create && perms.read && perms.update && perms.delete
          );
        }) &&
        reports.every((module) => {
          const perms = updated["report_" + module.id];
          return (
            perms && perms.create && perms.read && perms.update && perms.delete
          );
        }) &&
        dashboards.every((module) => {
          const perms = updated["dashboard_" + module.id];
          return (
            perms && perms.create && perms.read && perms.update && perms.delete
          );
        });
      setAllAccess(allEnabled);

      return updated;
    });
  };

  const handleSave = async () => {
    let isStar = true;

    modules.forEach((module) => {
      const perms = permissions[module.id];

      if (!perms.read || !perms.create || !perms.update || !perms.delete) {
        isStar = false;
      }
    });
    reports.forEach((module) => {
      const perms = permissions["report_" + module.id];

      if (!perms.read || !perms.create || !perms.update || !perms.delete) {
        isStar = false;
      }
    });
    dashboards.forEach((module) => {
      const perms = permissions["dashboard_" + module.id];

      if (!perms.read || !perms.create || !perms.update || !perms.delete) {
        isStar = false;
      }
    });

    // delete old permissions

    const responseAcc = await postUser("crud/list", {
      filters: {
        role_id: role.id,
      },
      module: "role_access",
      page: "1",
      per_page: "100",
      sort_direction: "+",
      sort_field: "id",
    });
    for (const acc of responseAcc.data) {
      deleteUser("crud/delete", {
        module: "role_access",
        id: acc.id,
      }).catch((error) =>
        console.error("Failed to delete role permissions:", error),
      );
    }

    if (isStar) {
      // create star permission

      postUser("crud/upsert", {
        module: "role_access",
        body: {
          role_id: role.id,
          access: "*",
        },
      }).catch((error) =>
        console.error("Failed to delete role permissions:", error),
      );
    } else {
      // crate permissions for each module
      for (const key of Object.keys(permissions)) {
        if (
          permissions[key].read ||
          permissions[key].create ||
          permissions[key].update ||
          permissions[key].delete
        ) {
          const [module, module_id] = key.includes("_")
            ? key.split("_")
            : ["module", key];

          console.log("key", key);
          postUser("crud/upsert", {
            module: "role_access",
            body: {
              role_id: role.id,
              module: module,
              module_id,
              access: Object.keys(permissions[key])
                .filter(
                  (perm) =>
                    permissions[key][perm as "create" | "read"] ?? false,
                )
                .join(","),
            },
          });
        }
      }
    }

    ToastService.success(t("permissionsSaved"));
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loader}>{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <IconButton
            onClick={() => navigate("/roles")}
            className={styles.backButton}
          >
            <ArrowBackIcon />
          </IconButton>
          <div>
            <h1>{role?.name || t("editRole")}</h1>
            <p className={styles.subtitle}>
              {role?.description || t("manageRolePermissions")}
            </p>
          </div>
        </div>
        <button onClick={handleSave} className={styles.saveButton}>
          {t("save")}
        </button>
      </div>

      <div className={styles.content}>
        <div className={`${styles.permissionRow} ${styles.allAccessRow}`}>
          <div className={styles.moduleInfo}>
            <div className={styles.moduleIcon}>
              <StarIcon />
            </div>
            <div className={styles.moduleName}>{t("fullAccess")}</div>
          </div>
          <div className={styles.permissions}>
            <button
              onClick={handleAllAccessToggle}
              className={`${styles.allAccessButton} ${allAccess ? styles.allAccessButtonActive : styles.allAccessButtonInactive}`}
            >
              <StarIcon className={styles.buttonIcon} />
              <span>{allAccess ? t("enabled") : t("disabled")}</span>
            </button>
          </div>
        </div>

        <h1>{t('dashboard')}</h1>
        {dashboards.map((module) => (
          <div key={"dashboard_" + module.id} className={styles.permissionRow}>
            <div className={styles.moduleInfo}>
              <div className={styles.moduleIcon}>
                <FontAwesomeIcon icon={getIcon("file")} />
              </div>
              <div className={styles.moduleName}>{module.name}</div>
            </div>
            <div className={styles.permissions}>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={
                    permissions["dashboard_" + module.id]?.create || false
                  }
                  onChange={() =>
                    handlePermissionChange("dashboard_" + module.id, "create")
                  }
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("create")}</span>
              </div>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={permissions["dashboard_" + module.id]?.read || false}
                  onChange={() =>
                    handlePermissionChange("dashboard_" + module.id, "read")
                  }
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("read")}</span>
              </div>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={
                    permissions["dashboard_" + module.id]?.update || false
                  }
                  onChange={() =>
                    handlePermissionChange("dashboard_" + module.id, "update")
                  }
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("update")}</span>
              </div>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={
                    permissions["dashboard_" + module.id]?.delete || false
                  }
                  onChange={() =>
                    handlePermissionChange("dashboard_" + module.id, "delete")
                  }
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("delete")}</span>
              </div>
            </div>
          </div>
        ))}

        <h1>{t('reports')}</h1>
        {reports.map((module) => (
          <div key={"report_" + module.id} className={styles.permissionRow}>
            <div className={styles.moduleInfo}>
              <div className={styles.moduleIcon}>
                <FontAwesomeIcon icon={getIcon("file")} />
              </div>
              <div className={styles.moduleName}>{module.name}</div>
            </div>
            <div className={styles.permissions}>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={permissions["report_" + module.id]?.create || false}
                  onChange={() =>
                    handlePermissionChange("report_" + module.id, "create")
                  }
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("create")}</span>
              </div>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={permissions["report_" + module.id]?.read || false}
                  onChange={() =>
                    handlePermissionChange("report_" + module.id, "read")
                  }
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("read")}</span>
              </div>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={permissions["report_" + module.id]?.update || false}
                  onChange={() =>
                    handlePermissionChange("report_" + module.id, "update")
                  }
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("update")}</span>
              </div>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={permissions["report_" + module.id]?.delete || false}
                  onChange={() =>
                    handlePermissionChange("report_" + module.id, "delete")
                  }
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("delete")}</span>
              </div>
            </div>
          </div>
        ))}

        <h1>{t('modules')}</h1>
        {modules.map((module) => (
          <div key={module.id} className={styles.permissionRow}>
            <div className={styles.moduleInfo}>
              <div className={styles.moduleIcon}>
                <FontAwesomeIcon icon={getIcon("file")} />
              </div>
              <div className={styles.moduleName}>{module.name}</div>
            </div>
            <div className={styles.permissions}>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={permissions[module.id]?.create || false}
                  onChange={() => handlePermissionChange(module.id, "create")}
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("create")}</span>
              </div>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={permissions[module.id]?.read || false}
                  onChange={() => handlePermissionChange(module.id, "read")}
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("read")}</span>
              </div>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={permissions[module.id]?.update || false}
                  onChange={() => handlePermissionChange(module.id, "update")}
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("update")}</span>
              </div>
              <div className={styles.permissionItem}>
                <Checkbox
                  checked={permissions[module.id]?.delete || false}
                  onChange={() => handlePermissionChange(module.id, "delete")}
                  className={styles.checkbox}
                />
                <span className={styles.permissionLabel}>{t("delete")}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
